import * as zod from 'zod';

import { CartesianPose, ZERO_POSE } from '@sb/geometry';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types/CameraConfiguration';
import { CameraIntegration } from '@sb/integrations/types/cameraTypes';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import { validateCodeExpression } from '@sb/remote-control/util/expressions/validateCodeExpression';
import {
  DEFAULT_REGION_OF_INTEREST,
  Detection3DPoseEstimationConfig,
  Expression,
  LocateMethod,
  RegionOfInterest,
  ResultOrdering,
} from '@sb/routine-runner';
import {
  DEFAULT_MAX_TILT_ANGLE,
  PlaneDetectionMode,
  RansacParams,
} from '@sb/routine-runner/Step/Locate/Arguments';

export namespace LocateStep {
  export const name = 'Locate';
  export const description = 'Locate via vision';
  export const librarySection = Step.LibrarySection.Vision;
  export const argumentKind = 'Locate';
  export const featureFlag = 'locateStep';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    // For camera-bot-cpp mode
    cameraEquipmentId: zod.string().optional(),
    // For non-camera-bot-cpp mode
    camera: CameraIntegration.optional(),
    regionOfInterest: RegionOfInterest.default(DEFAULT_REGION_OF_INTEREST),
    method: LocateMethod,
    positionListName: zod.string().default('Locate step results'),
    resultsLimit: zod.number().default(10),
    resultOrdering: ResultOrdering.optional(),
    transform: CartesianPose.default(ZERO_POSE),
    // Grasp frame the `transform` offset rides in (object / plane / world).
    // Preview-only, like `projectionMode` below: `toRoutineRunner`
    // does not forward it (the standalone Locate step keeps its plane-relative
    // transform), but `getLocateArgsFromLocateStepArgs` threads it into the
    // wizard's live-preview locate call so Results reflect the chosen frame.
    graspFrame: zod.enum(['object', 'plane', 'world']).optional(),
    planeID: zod.string().optional(),
    objectHeight: zod.number().default(0),
    planeDetectionMode: PlaneDetectionMode,
    ransacParams: RansacParams.optional(),
    maxTiltAngle: zod
      .number()
      .min(1)
      .max(90)
      .optional()
      .default(DEFAULT_MAX_TILT_ANGLE),
    disablePlaneOrientation: zod.boolean().optional().default(false),
    planeOpacity: zod.number().optional().default(0.5),
    // 3D-detection projection for the wizard's live preview. Optional and
    // unused by the routine Locate step runtime (toRoutineRunner does not
    // forward them); consumed by getLocateArgsFromLocateStepArgs for the
    // server-side live preview.
    projectionMode: zod.enum(['Manual', 'Detection3D']).optional(),
    detection3dConfig: Detection3DPoseEstimationConfig.optional(),
    // Canonical orientation frame persisted at teach time. Preview-only, like
    // `projectionMode`/`graspFrame` above: `toRoutineRunner` does not forward
    // it, but `getLocateArgsFromLocateStepArgs` threads it into the wizard's
    // live-preview locate call so an already-saved skill's reference rides into
    // the plane-projection request when editing. Null when never taught.
    orientationReference: zod
      .object({
        referenceContour: zod.array(zod.array(zod.number())),
      })
      .nullable()
      .optional(),
    captureConfig: zod
      .object({
        saveEquipmentId: zod.string(),
        saveFilepath: Expression,
        saveMetadata: zod.boolean().default(false),
      })
      .optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    stepConfiguration,
    routine: { space },
    equipment,
    globalSpace,
  }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    const isManualMode = (args.planeDetectionMode ?? 'Manual') === 'Manual';

    if (isManualMode) {
      if (!args.planeID) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'No workplane selected',
          fieldId: 'planeID',
        });
      }

      const allSpace = [...globalSpace, ...space];

      if (
        !allSpace.some(
          (item) => item.id === args.planeID && item.kind === 'plane',
        )
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Selected workplane is not valid',
          fieldId: 'planeID',
        });
      }
    }

    if (!args.positionListName) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position list name is not configured',
        fieldId: 'positionListName',
      });
    }

    // Validate camera equipment if it is set
    if (args.cameraEquipmentId) {
      const cameraEquipment = equipment.find(
        (e) => e.id === args.cameraEquipmentId,
      );

      if (cameraEquipment == null) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Camera equipment is not set',
          fieldId: 'cameraEquipmentId',
        });
      }

      if (cameraEquipment.config.kind !== 'Camera') {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Camera equipment must be a camera.',
          fieldId: 'cameraEquipmentId',
        });
      }

      const cameraConfig = cameraEquipment.config as CameraConfiguration;

      if (!cameraConfig.intrinsicsId) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Intrinsics on camera equipment must be set.',
          fieldId: 'cameraEquipmentId',
        });
      }

      if (!cameraConfig.extrinsicsId) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Extrinsics on camera equipment must be set.',
          fieldId: 'cameraEquipmentId',
        });
      }
    }

    // Validate captureConfig when present
    if (args.captureConfig) {
      const { captureConfig } = args;

      if (!captureConfig.saveEquipmentId) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Save equipment ID is required',
          fieldId: 'captureConfig.saveEquipmentId',
        });
      }

      const storageEquipment = equipment.find(
        (e) => e.id === captureConfig.saveEquipmentId,
      );

      if (!storageEquipment) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Storage equipment not found',
          fieldId: 'captureConfig.saveEquipmentId',
        });
      }

      if (
        storageEquipment.config.kind !== 'FTPServer' &&
        storageEquipment.config.kind !== 'S3Bucket'
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Storage equipment must be an FTP Server or S3 Bucket',
          fieldId: 'captureConfig.saveEquipmentId',
        });
      }

      if (
        captureConfig.saveFilepath &&
        captureConfig.saveFilepath.kind === 'constant' &&
        captureConfig.saveFilepath.value === ''
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Save file path is required',
          fieldId: 'captureConfig.saveFilepath',
        });
      }

      if (
        captureConfig.saveFilepath &&
        ((captureConfig.saveFilepath.kind === 'constant' &&
          !captureConfig.saveFilepath.value.toString().endsWith('.png')) ||
          (captureConfig.saveFilepath.kind === 'JavaScript' &&
            !captureConfig.saveFilepath.expression.endsWith('.png"')) ||
          (captureConfig.saveFilepath.kind === 'Python' &&
            !captureConfig.saveFilepath.expression.endsWith('.png"')))
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Save file path must end with .png',
          fieldId: 'captureConfig.saveFilepath',
        });
      }

      validateCodeExpression(
        captureConfig.saveFilepath,
        'captureConfig.saveFilepath',
        'Save filepath',
      );
    }

    const { method } = args;

    switch (method.kind) {
      case 'BlobDetection2D': {
        if (method.settings.maxThreshold < method.settings.minThreshold) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max threshold may not be smaller than min threshold',
          });
        }

        if (method.settings.maxArea < method.settings.minArea) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max area may not be smaller than min area',
          });
        }

        if (
          method.settings.filterByCircularity &&
          method.settings.maxCircularity < method.settings.minCircularity
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max circularity may not be smaller than min circularity',
          });
        }

        break;
      }

      case 'ShapeDetection2D': {
        if (method.settings.maxScale < method.settings.minScale) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max scale may not be smaller than min scale',
          });
        }

        if (!method.templateImage.storageID) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Template image is not set',
          });
        }

        break;
      }

      case 'CustomCheckpointDetection2D': {
        if (!method.settings.gpuTaskRunnerId) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'No GPU Task Runner selected',
            fieldId: 'method.settings.gpuTaskRunnerId',
          });
        }

        const equipmentConfig = equipment.find(
          (e) => e.id === method.settings.gpuTaskRunnerId,
        );

        if (!equipmentConfig) {
          throw new ActionRequiredError({
            kind: 'missingEquipment',
            message: `Unable to find GPU Task Runner with id ${method.settings.gpuTaskRunnerId} in equipment manager.`,
            fieldId: 'method.settings.gpuTaskRunnerId',
          });
        }

        if (method.settings.modelSource === 'roboflow') {
          if (!method.settings.roboflowId) {
            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message: 'No Roboflow model selected',
              fieldId: 'method.settings.roboflowId',
            });
          }
        } else if (!method.settings.modelPath) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'No model selected',
            fieldId: 'method.settings.modelPath',
          });
        }

        if (!method.settings.modelType) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'No model type selected',
            fieldId: 'method.settings.modelType',
          });
        }

        break;
      }
      case 'TextPromptDetection2D': {
        if (!method.settings.textInput) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Text prompt input is empty',
            fieldId: 'method.settings.textInput',
          });
        }

        if (method.settings.gpuTaskRunnerId) {
          const equipmentConfig = equipment.find(
            (e) => e.id === method.settings.gpuTaskRunnerId,
          );

          if (!equipmentConfig) {
            throw new ActionRequiredError({
              kind: 'missingEquipment',
              message: `Unable to find GPU Task Runner with id ${method.settings.gpuTaskRunnerId} in equipment manager.`,
              fieldId: 'method.settings.gpuTaskRunnerId',
            });
          }
        }

        break;
      }

      default:
        break;
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'Locate',
      args: {
        camera: args.camera,
        cameraEquipmentId: args.cameraEquipmentId,
        regionOfInterest: args.regionOfInterest,
        method: args.method,
        positionListID: stepData.id,
        filters: {
          resultsLimit: args.resultsLimit,
        },
        transform: args.transform,
        planeID: args.planeID,
        objectHeight: args.objectHeight,
        planeDetectionMode: args.planeDetectionMode,
        ransacParams: args.ransacParams,
        maxTiltAngle: args.maxTiltAngle,
        disablePlaneOrientation: args.disablePlaneOrientation,
        resultOrdering: args.resultOrdering,
        captureConfig: args.captureConfig,
      },
    };
  };
}

LocateStep satisfies Step.StepKindInfo;
